import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface BrandFormData {
  code: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  website?: string;
  country?: string;
  isActive: boolean;
  sortOrder: number;
}

interface TradingBrand {
  id: string;
  code: string;
  name: string;
  slug: string;
  description?: string | null;
  logo?: string | null;
  website?: string | null;
  country?: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface BrandModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brand?: TradingBrand;
}

export function BrandModal({ open, onOpenChange, brand }: BrandModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!brand;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BrandFormData>({
    defaultValues: {
      code: '',
      name: '',
      slug: '',
      description: '',
      logo: '',
      website: '',
      country: '',
      isActive: true,
      sortOrder: 0,
    },
  });

  const utils = trpc.useUtils();

  // Get next available code for new brands
  const { data: nextCodeData } = trpc.tradingBrand.getNextCode.useQuery(undefined, {
    enabled: open && !isEditing,
  });

  const createMutation = trpc.tradingBrand.create.useMutation({
    onSuccess: () => {
      utils.tradingBrand.list.invalidate();
      showSuccessToast(t('common.created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingBrand.update.useMutation({
    onSuccess: () => {
      utils.tradingBrand.list.invalidate();
      showSuccessToast(t('common.updated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (brand) {
        reset({
          code: brand.code,
          name: brand.name,
          slug: brand.slug,
          description: brand.description || '',
          logo: brand.logo || '',
          website: brand.website || '',
          country: brand.country || '',
          isActive: brand.isActive,
          sortOrder: brand.sortOrder,
        });
      } else {
        reset({
          code: nextCodeData || '',
          name: '',
          slug: '',
          description: '',
          logo: '',
          website: '',
          country: '',
          isActive: true,
          sortOrder: 0,
        });
      }
    }
  }, [open, brand, nextCodeData, reset]);

  // Auto-generate slug from name
  const watchName = watch('name');
  useEffect(() => {
    if (!isEditing && watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', slug);
    }
  }, [watchName, isEditing, setValue]);

  const onSubmit = (data: BrandFormData) => {
    const payload = {
      ...data,
      description: data.description || undefined,
      logo: data.logo || undefined,
      website: data.website || undefined,
      country: data.country || undefined,
    };

    if (isEditing && brand) {
      updateMutation.mutate({
        id: brand.id,
        data: payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('brands.editBrand') : t('brands.addBrand')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('brands.editBrand')
              : t('brands.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('brands.code')}</Label>
              <Input
                id="code"
                {...register('code', { required: t('common.required') })}
                placeholder="01"
                maxLength={2}
                disabled={isEditing}
              />
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
              <p className="text-xs text-muted-foreground">{t('brands.codeHelp')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">{t('brands.sortOrder')}</Label>
              <Input
                id="sortOrder"
                type="number"
                {...register('sortOrder', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('brands.name')}</Label>
            <Input
              id="name"
              {...register('name', { required: t('common.required') })}
              placeholder="Brand Name"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">{t('brands.slug')}</Label>
            <Input
              id="slug"
              {...register('slug', { required: t('common.required') })}
              placeholder="brand-name"
            />
            {errors.slug && (
              <p className="text-sm text-red-500">{errors.slug.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('brands.description')}</Label>
            <Textarea
              id="description"
              {...register('description')}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">{t('brands.website')}</Label>
              <Input
                id="website"
                type="url"
                {...register('website')}
                placeholder="https://example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t('brands.country')}</Label>
              <Input
                id="country"
                {...register('country')}
                placeholder="Country"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo">{t('brands.logo')}</Label>
            <Input
              id="logo"
              {...register('logo')}
              placeholder="https://example.com/logo.png"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              className="h-4 w-4 border border-border"
            />
            <Label htmlFor="isActive">{t('brands.active')}</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
